import React, { useEffect, useRef, useState } from "react";

import { useAppDispatch, useAppSelector } from "@store/hooks";
import instance from "@store/middleware/api";
import { setCheckoutStep, setPaymentData, setTermsAccepted, setWithdrawalWaiverAccepted } from "@store/reducers/cart";

import styled, { keyframes } from "styled-components";

import { ButtonLabel } from "@/components/ButtonGlyph";

import {
  Body,
  BottomFade,
  DesktopHeader,
  FirstSectionLabel,
  FooterActions,
  PhoneHeader,
  ScrollArea,
  ScrollChevron,
  ScrollHint,
  useScrollAffordance,
} from "./phoneTemplate";
import {
  BackBtn,
  BillingBlock,
  CheckboxBox,
  CheckboxLabel,
  CheckboxRow,
  IconCircle,
  PrimaryBtn,
  ReviewItem,
  ReviewItemLeft,
  ReviewItemName,
  ReviewItemPrice,
  ReviewItemQty,
  ReviewThumb,
  SectionLabel,
  SlidePanelFooter,
  StepSubtitle,
  StepTitle,
  TotalAmount,
  TotalRow,
  TotalsWrap,
} from "./styles";
import { VAT_RATE } from "./types";

const CartIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="#ebc28e" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="9" cy="20.5" r="1.3" fill="none" /><circle cx="17.4" cy="20.5" r="1.3" fill="none" /><path d="M2 3h3l2.4 12.1a1.7 1.7 0 0 0 1.7 1.4h8.5a1.7 1.7 0 0 0 1.7-1.4L21 7.5H6" />
  </svg>
);

const ConsentCheck = () => (
  <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="rgb(97, 84, 73)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const consentShake = keyframes`
  0%, 100% { transform: translateX(0); }
  10%, 30%, 50%, 70%, 90% { transform: translateX(-4px); }
  20%, 40%, 60%, 80% { transform: translateX(4px); }
`;

const ShakeWrap = styled.div<{ $shake: boolean }>`
  animation: ${({ $shake }) => ($shake ? consentShake : "none")} 0.5s ease;
`;

/* The three consents read as ONE block, not three stacked cards: a single
   surface with even internal spacing. CheckboxRow brings its own background
   and a 12px bottom margin, which is what made them look like separate boxes
   sitting at uneven distances — both are stripped inside here. */
const ConsentGroup = styled.div`
  background: rgba(235, 194, 142, 0.06);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(235, 194, 142, 0.14);
  border-radius: 8px;
  padding: 4px 2px;
  margin-bottom: 12px;
  display: flex;
  flex-direction: column;

  /* Hairline separators rather than gaps. The first attempt gave the panel the
     same tint the rows already had, so grouping them changed almost nothing
     visually — the rows still read as three cards. A divider states the
     grouping outright: one surface, three entries, identical spacing. */
  > * + * {
    border-top: 1px solid rgba(235, 194, 142, 0.12);
  }
`;

/* "Continue to Payment" wraps on a phone; the short form is used there only.
   Desktop keeps the explicit label. */
const LabelDesktop = styled.span`
  @media (max-width: 600px) { display: none; }
`;
const LabelPhone = styled.span`
  display: none;
  @media (max-width: 600px) { display: inline; }
`;

const ConsentRow = styled(CheckboxRow)`
  background: transparent;
  backdrop-filter: none;
  -webkit-backdrop-filter: none;
  margin-bottom: 0;
  border-radius: 0;
  padding: 14px 16px;
`;

const ReviewView: React.FC = () => {
  const dispatch = useAppDispatch();
  const { items, tipAmount, tipType, billingDetails, isLoggedInCheckout, termsAccepted, withdrawalWaiverAccepted } = useAppSelector(
    (state) => state.cart,
  );
  const user = useAppSelector((state) => state.user);
  const isAuthenticated = !!(user as any)?.user?.email;
  const isGuest = !isAuthenticated && !isLoggedInCheckout;

  const subtotal = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const tip = tipType === "skip" ? 0 : tipAmount;
  const vat = subtotal - subtotal / (1 + VAT_RATE);
  const total = subtotal + tip;

  const handleBack = () => {
    dispatch(setCheckoutStep("tip"));
  };

  const [paying, setPaying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Stable per-attempt idempotency key for the PaymentIntent — keyed to the
  // amount so a re-click (same cart) reuses the SAME PI (one charge), while a
  // cart change regenerates it (a new PI for the new amount).
  const idemKeyRef = useRef<{ amt: number; key: string }>({ amt: -1, key: "" });
  // Auto-scroll payment errors into view (they render at the bottom of the
  // scroll body, below the fold).
  const errorRef = useRef<HTMLDivElement>(null);
  useEffect(() => { if (error) errorRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }); }, [error]);
  const bodyRef = useRef<HTMLDivElement>(null);
  const canScrollDown = useScrollAffordance(bodyRef);

  // Consent lives HERE, not on CheckoutView. CheckoutView is only rendered for
  // guests — it is a login/signup gate that authenticated users skip entirely
  // (see CartView.handleProceed), so boxes placed there were never shown to a
  // logged-in buyer, while PaymentView posted `true` for them regardless.
  // Review is the last step before payment and EVERY one-time buyer passes
  // through it, guest or not.
  const [consentAttempted, setConsentAttempted] = useState(false);
  const [shakeTerms, setShakeTerms] = useState(false);
  const [shakeWithdrawal, setShakeWithdrawal] = useState(false);
  const consentComplete = termsAccepted && withdrawalWaiverAccepted;

  const handleContinueToPayment = async () => {
    if (!consentComplete) {
      setConsentAttempted(true);
      if (!termsAccepted) { setShakeTerms(true); setTimeout(() => setShakeTerms(false), 500); }
      if (!withdrawalWaiverAccepted) { setShakeWithdrawal(true); setTimeout(() => setShakeWithdrawal(false), 500); }
      return;
    }
    setPaying(true);
    setError(null);
    try {
      // Create a PaymentIntent for the total amount (order created on pay).
      // Stable, amount-keyed idempotency key so a re-click reuses the same PI
      // (one charge) and a changed cart mints a fresh one.
      if (idemKeyRef.current.amt !== total) {
        idemKeyRef.current = { amt: total, key: window.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}` };
      }
      const stripeResp = await instance.post("/stripe/create-payment-intent", {
        amount: total,
        customerEmail: isGuest ? billingDetails.email : undefined,
        idempotencyKey: idemKeyRef.current.key,
      });

      dispatch(setPaymentData({
        clientSecret: stripeResp.data.clientSecret,
        paymentIntentId: stripeResp.data.paymentIntentId,
        orderId: null,
      }));
      dispatch(setCheckoutStep("payment"));
      setPaying(false);
    } catch (err: any) {
      console.error("Payment setup failed:", err);
      setError("Something went wrong. Please try again.");
      setPaying(false);
    }
  };

  return (
    <>
      <PhoneHeader>
        <h2>Order Summary</h2>
      </PhoneHeader>

      <ScrollArea>
      <Body ref={bodyRef}>
      <DesktopHeader>
        <IconCircle>
          <CartIcon />
        </IconCircle>
        <StepTitle>Order Summary</StepTitle>
        <StepSubtitle>Please review your order before payment</StepSubtitle>
      </DesktopHeader>

      <FirstSectionLabel>
        <span>Items</span>
      </FirstSectionLabel>

      {items.map((item) => (
        <ReviewItem key={`${item.trackId}-${item.licenseType}`}>
          <ReviewItemLeft>
            <ReviewThumb src={item.coverUrl} alt={item.trackName} />
            <div style={{ display: "flex", flexDirection: "column", gap: "0" }}>
              <ReviewItemName>{item.trackName}</ReviewItemName>
              <ReviewItemQty>
                {item.quantity}x {item.licenseLabel.replace(/\s*\(\d+\)\s*$/, "")}
              </ReviewItemQty>
            </div>
          </ReviewItemLeft>
          <ReviewItemPrice>${(item.price * item.quantity).toFixed(2)}</ReviewItemPrice>
        </ReviewItem>
      ))}

      {isGuest && billingDetails.email && (
        <>
          <SectionLabel>
            <span>Billing Address</span>
          </SectionLabel>
          <BillingBlock>
            <p>
              {billingDetails.firstName} {billingDetails.lastName}
            </p>
            <p>{billingDetails.email}</p>
            <p>{billingDetails.address}</p>
            <p>
              {billingDetails.zipCode}, {billingDetails.country}
            </p>
          </BillingBlock>
        </>
      )}

      <SectionLabel>
        <span>Order Total</span>
      </SectionLabel>

      <TotalsWrap style={{ marginTop: 0, paddingTop: 0, borderTop: "none" }}>
        <TotalRow>
          <span>Subtotal (VAT included)</span>
          <TotalAmount>${subtotal.toFixed(2)}</TotalAmount>
        </TotalRow>
        <TotalRow>
          <span>Tip</span>
          <TotalAmount>${tip.toFixed(2)}</TotalAmount>
        </TotalRow>
        <TotalRow>
          <span>VAT ({(VAT_RATE * 100).toFixed(0)}%)</span>
          <TotalAmount>${vat.toFixed(2)}</TotalAmount>
        </TotalRow>
        <div style={{ height: "1px", background: "rgba(235,194,142,0.15)", margin: "8px 0" }} />
        <TotalRow $bold>
          <span>Total</span>
          <TotalAmount $bold>${total.toFixed(2)}</TotalAmount>
        </TotalRow>
      </TotalsWrap>

      {error && (
        <div ref={errorRef} style={{
          padding: "10px 14px",
          borderRadius: "8px",
          background: "rgba(228, 122, 106, 0.12)",
          border: "1px solid rgba(228, 122, 106, 0.3)",
          color: "#e47a6a",
          fontSize: "0.875rem",
          fontFamily: "'Raleway', sans-serif",
          marginBottom: "16px",
        }}>
          {error}
        </div>
      )}
      <SectionLabel><span>Review &amp; Accept</span></SectionLabel>

      <ConsentGroup>
        {/* The content waiver — "my video is not focused on music or ambient
            content" — was removed 2026-08-02 (owner's decision). The YouTube
            Licence Terms are now named in the checkbox below, so that is where
            the legal timestamp comes from, and the Music Use check enforces the
            content rule for real rather than on the customer's word. */}
        <ShakeWrap $shake={shakeTerms}>
          <ConsentRow
            onClick={() => dispatch(setTermsAccepted(!termsAccepted))}
            style={{ border: `1px solid ${consentAttempted && !termsAccepted ? "#e47a6a" : "transparent"}` }}
          >
            <CheckboxBox $checked={termsAccepted}><ConsentCheck /></CheckboxBox>
            <CheckboxLabel>
              I agree to the <a href="/terms-of-use" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} style={{ color: "#9cd3fd" }}>Terms of Use</a>,{" "}
              <a href="/privacy" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} style={{ color: "#9cd3fd" }}>Privacy Policy</a>,{" "}
              <a href="/licence-terms" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} style={{ color: "#9cd3fd" }}>YouTube Licence Terms</a>, and{" "}
              <a href="/refund-policy" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} style={{ color: "#9cd3fd" }}>Refund Policy</a>.
            </CheckboxLabel>
          </ConsentRow>
        </ShakeWrap>

        {/* §18 FAGG wants the consent to immediate performance and the
            acknowledgement of losing the withdrawal right as an EXPRESS,
            separate act — not a clause folded into the terms box. */}
        <ShakeWrap $shake={shakeWithdrawal}>
          <ConsentRow
            onClick={() => dispatch(setWithdrawalWaiverAccepted(!withdrawalWaiverAccepted))}
            style={{ border: `1px solid ${consentAttempted && !withdrawalWaiverAccepted ? "#e47a6a" : "transparent"}` }}
          >
            <CheckboxBox $checked={withdrawalWaiverAccepted}><ConsentCheck /></CheckboxBox>
            <CheckboxLabel>
              I want my licence delivered immediately and acknowledge that I thereby lose my 14-day right of withdrawal under EU consumer law.
            </CheckboxLabel>
          </ConsentRow>
        </ShakeWrap>
      </ConsentGroup>

      </Body>
      <BottomFade $visible={canScrollDown} aria-hidden="true"><ScrollHint><span>Scroll</span><ScrollChevron /></ScrollHint></BottomFade>
      </ScrollArea>

      <SlidePanelFooter>
        <FooterActions>
          <BackBtn onClick={handleBack}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5" /><polyline points="12 19 5 12 12 5" /></svg> Back</BackBtn>
          <PrimaryBtn onClick={handleContinueToPayment} disabled={paying} $dimmed={!consentComplete || paying}>
            {paying ? "Setting up payment..." : (
              <ButtonLabel glyph="arrowRight" muted={!consentComplete}>
                <LabelDesktop>Continue to Payment</LabelDesktop>
                <LabelPhone>Continue</LabelPhone>
              </ButtonLabel>
            )}
          </PrimaryBtn>
        </FooterActions>
      </SlidePanelFooter>
    </>
  );
};

export default ReviewView;
